import React, { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { BeveledPanel } from '../Equipment/FabProcessTool/BeveledPanel';
import { FOUP } from '../FOUP/FOUP';
import { AMHS_PALETTE } from '../types';
import { getStockerMaterials } from './StockerMaterials';
import {
  resolveStockerRobotParkState,
  resolveStockerRobotRail,
} from './stockerTransferRobotMapping';
import { stockerTransferRobotStore } from './stockerTransferRobotStore';
import type { StockerDimensions, StockerRobotState } from './stockerTypes';
import { cameraDragStore } from '../../../../store/cameraDragStore';

export type StockerRobotProps = {
  dims: StockerDimensions;
  /** When set, pose is read every frame from the transfer store for smooth motion. */
  elementId?: string;
  robotState?: StockerRobotState;
  opacity?: number;
  foupScale?: number;
};

/**
 * Centered vertical lifter mast with carriage + fork holder pad.
 * Parks at the bottom (load-port row) until an OHT transfer drives motion.
 */
export const StockerRobot: React.FC<StockerRobotProps> = ({
  dims,
  elementId,
  robotState,
  opacity = 1,
  foupScale = 1,
}) => {
  const mats = useMemo(() => getStockerMaterials(opacity), [opacity]);
  const { width, height, depth } = dims;

  const carriageRef = useRef<THREE.Group>(null);
  const forkRef = useRef<THREE.Group>(null);
  const foupRef = useRef<THREE.Group>(null);
  const parkState = useMemo(() => resolveStockerRobotParkState(dims), [dims]);

  const { railBottom, railH } = useMemo(() => resolveStockerRobotRail(dims), [dims]);
  const railTop = railBottom + railH;
  const railY = (railTop + railBottom) * 0.5;
  const railR = width * 0.024;
  const trayD = depth * 0.11;
  const trayW = width * 0.11;
  const forkDeck = Math.max(0.22, foupScale * 0.048);

  const goalRef = useRef({
    x: parkState.carriageX ?? 0,
    y: parkState.carriageY ?? railBottom,
    forkZ: parkState.forkLocalZ ?? 0,
    carrying: false,
  });

  useFrame((_, dt) => {
    if (cameraDragStore.isDragging()) return;
    const state =
      elementId != null
        ? stockerTransferRobotStore.get(elementId)
        : robotState ?? parkState;

    const goalX = state.carriageX ?? 0;
    const goalY =
      state.carriageY ??
      railBottom + (state.carriageT ?? parkState.carriageT ?? 0.04) * railH;
    const goalForkZ = state.forkLocalZ ?? 0;
    goalRef.current = {
      x: goalX,
      y: goalY,
      forkZ: goalForkZ,
      carrying: Boolean(state.carryingFoup),
    };

    const transferring =
      Boolean(state.carryingFoup) ||
      (state.status != null && state.status !== 'idle');
    const carriageFollow = transferring ? 1 : 1 - Math.exp(-5 * dt);
    const forkFollow = transferring ? 1 : 1 - Math.exp(-7 * dt);

    if (carriageRef.current) {
      carriageRef.current.position.x = THREE.MathUtils.lerp(
        carriageRef.current.position.x,
        goalX,
        carriageFollow
      );
      carriageRef.current.position.y = THREE.MathUtils.lerp(
        carriageRef.current.position.y,
        goalY,
        carriageFollow
      );
    }
    if (forkRef.current) {
      forkRef.current.position.z = THREE.MathUtils.lerp(
        forkRef.current.position.z,
        goalForkZ,
        forkFollow
      );
    }
    if (foupRef.current) {
      foupRef.current.visible = goalRef.current.carrying;
    }
  });

  const initialY =
    robotState?.carriageY ??
    railBottom + (robotState?.carriageT ?? parkState.carriageT ?? 0.04) * railH;

  return (
    <group name="StockerRobot" position={[0, 0, dims.lifterAisleZ]}>
      <mesh position={[0, railY, 0]} material={mats.whiteMetal} castShadow>
        <cylinderGeometry args={[railR, railR * 1.05, railH, 16]} />
      </mesh>
      <mesh position={[0, railY, 0]} material={mats.brushedMetal}>
        <cylinderGeometry args={[railR * 0.52, railR * 0.52, railH * 0.98, 12]} />
      </mesh>
      <BeveledPanel
        size={[railR * 0.65, railH * 0.94, railR * 0.4]}
        material={mats.cyanLed}
        position={[railR * 0.72, railY, 0]}
        smoothness={2}
      />

      <group ref={carriageRef} position={[parkState.carriageX ?? 0, initialY, 0]}>
        <BeveledPanel
          size={[trayW * 1.08, height * 0.038, trayD * 1.05]}
          material={mats.whiteMetal}
          castShadow
        />
        <BeveledPanel
          size={[trayW * 0.72, height * 0.022, trayD * 0.82]}
          material={mats.brushedMetal}
          position={[0, height * 0.016, 0]}
        />

        <group ref={forkRef}>
          {/* Holder pad — cradles FOUP from OHT and carries it to the rack slot */}
          <BeveledPanel
            size={[trayW * 1.12, height * 0.014, trayD * 1.18]}
            material={mats.brushedMetal}
            position={[0, -height * 0.006, 0]}
            castShadow
          />
          <BeveledPanel
            size={[trayW * 0.94, height * 0.032, trayD * 0.08]}
            material={mats.darkMetal}
            position={[0, -height * 0.004, trayD * 0.52]}
          />
          <BeveledPanel
            size={[trayW * 0.94, height * 0.032, trayD * 0.08]}
            material={mats.darkMetal}
            position={[0, -height * 0.004, -trayD * 0.52]}
          />
          <BeveledPanel
            size={[trayW * 0.08, height * 0.036, trayD * 0.92]}
            material={mats.cyanLed}
            position={[-trayW * 0.5, height * 0.002, 0]}
          />
          <BeveledPanel
            size={[trayW * 0.08, height * 0.036, trayD * 0.92]}
            material={mats.cyanLed}
            position={[trayW * 0.5, height * 0.002, 0]}
          />

          <group ref={foupRef} visible={false}>
            <FOUP
              scale={foupScale}
              opacity={opacity}
              color={AMHS_PALETTE.stockerRawFoup}
              position={[0, forkDeck, 0]}
            />
          </group>
        </group>
      </group>
    </group>
  );
};

export default StockerRobot;
